import React from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from './SiteHeader';
import useScrollReveal from '../hooks/useScrollReveal';

const GREEN = '#1e5631';
const GREEN_DARK = '#0f3d20';
const GOLD = '#d4af37';

function Reveal({ as: Tag = 'div', className = '', style, children, ...rest }) {
  const ref = useScrollReveal();
  return (
    <Tag ref={ref} className={`reveal ${className}`} style={style} {...rest}>
      {children}
    </Tag>
  );
}

export default function LandingPage() {
  return (
    <div className="landing-page public-page">
      <SiteHeader />

      {/* ── Hero ── */}
      <section style={{ position: 'relative', overflow: 'hidden', padding: '90px 40px 100px' }}>
        <div className="brand-blob" style={{ width: 320, height: 320, bottom: -120, right: -80, background: GOLD, opacity: 0.5 }} />
        <div style={{
          position: 'relative', zIndex: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: '60px', flexWrap: 'wrap', maxWidth: '1100px', margin: '0 auto',
        }}>
          <div style={{ flex: '1', minWidth: '320px' }}>
            <span style={{
              display: 'inline-block', background: 'rgba(212,175,55,0.15)', color: '#a3791d',
              padding: '6px 14px', borderRadius: '999px', fontSize: '13px', fontWeight: 700,
              letterSpacing: '0.4px', marginBottom: '18px',
            }}>
              🌿 100% Fresh · Zero Preservatives
            </span>
            <h2 style={{ fontSize: '48px', color: GREEN_DARK, marginBottom: '20px', fontWeight: 800, lineHeight: 1.15 }}>
              Fresh Juice, Fully Automated.
            </h2>
            <p style={{ fontSize: '18px', color: '#5c6b64', marginBottom: '32px', maxWidth: '480px' }}>
              Chill Ras operates state-of-the-art physical juice vending machines. Scan, pay, and enjoy
              freshly dispensed juices like Sugarcane and Aam Panna in seconds.
            </p>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              {[['📱', '1. Scan QR'], ['💳', '2. Pay'], ['🥤', '3. Enjoy']].map(([icon, title]) => (
                <div key={title} className="lift-card" style={{
                  padding: '14px 18px', backgroundColor: '#fff', borderRadius: '10px',
                  boxShadow: '0 4px 15px rgba(15,61,32,0.08)', display: 'flex', alignItems: 'center', gap: '10px',
                }}>
                  <span style={{ fontSize: '22px' }}>{icon}</span>
                  <span style={{ fontWeight: 700, color: GREEN_DARK, fontSize: '14px' }}>{title}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ flex: '0 0 auto' }}>
            <img
              src="/machine-product.png"
              alt="Chill Ras juice vending machine"
              style={{ maxWidth: '340px', width: '100%', filter: 'drop-shadow(0 25px 40px rgba(15,61,32,0.28))' }}
            />
          </div>
        </div>
      </section>

      {/* ── Partner Program banner ── */}
      <Reveal as="section" style={{ padding: '0 40px 80px' }}>
        <Link
          to="/partner-program"
          className="lift-card cta-pulse"
          style={{
            textDecoration: 'none', maxWidth: '1000px', margin: '0 auto',
            background: `linear-gradient(120deg, ${GREEN_DARK}, ${GREEN})`,
            borderRadius: '20px', padding: '44px 48px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: '24px',
          }}
        >
          <div>
            <div style={{ color: GOLD, fontWeight: 700, fontSize: '13px', letterSpacing: '0.6px', marginBottom: '8px' }}>
              CHILLRAS PARTNER PROGRAM
            </div>
            <h3 style={{ color: '#fff', fontSize: '28px', margin: '0 0 8px', fontWeight: 800 }}>
              Earn extra income with zero investment
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.85)', margin: 0, fontSize: '15px' }}>
              Host a Chill Ras kiosk at your shop — 15% commission, full-service maintenance, no setup cost.
            </p>
          </div>
          <span style={{
            background: GOLD, color: GREEN_DARK, fontWeight: 800, fontSize: '15px',
            padding: '14px 26px', borderRadius: '10px', whiteSpace: 'nowrap',
          }}>
            Explore the Program →
          </span>
        </Link>
      </Reveal>

      {/* ── Video ── */}
      <Reveal as="section" style={{ padding: '0 40px 60px', textAlign: 'center' }}>
        <video
          autoPlay
          loop
          muted
          playsInline
          controls
          preload="auto"
          poster="/machine-hero.jpg"
          style={{
            width: '100%', maxWidth: '720px', borderRadius: '18px',
            boxShadow: '0 20px 50px rgba(15,61,32,0.22)', display: 'block', margin: '0 auto',
          }}
        >
          <source src="/video_advertisement.mp4" type="video/mp4" />
        </video>
      </Reveal>

      {/* ── Locations ── */}
      <Reveal as="section" id="locations" style={{ padding: '20px 40px 90px', textAlign: 'center' }}>
        <h2 style={{ fontSize: '34px', color: GREEN_DARK, marginBottom: '40px', fontWeight: 800 }}>Our Kiosk Locations</h2>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '30px', flexWrap: 'wrap' }}>
          {[['📍 Noida Sector 11', 'Chill Ras Kiosk #11\nSector 11 Market area\nNoida, UP'],
          ['📍 Noida Sector 12', 'Chill Ras Kiosk #12\nSector 12 Main Road\nNoida, UP']].map(([loc, addr]) => (
            <div key={loc} className="lift-card" style={{
              textAlign: 'left', padding: '30px', backgroundColor: '#fff',
              borderRadius: '14px', minWidth: '280px', boxShadow: '0 4px 20px rgba(15,61,32,0.06)',
            }}>
              <h3 style={{ color: GREEN, marginTop: 0, fontSize: '19px' }}>{loc}</h3>
              <p style={{ whiteSpace: 'pre-line', color: '#5c6b64' }}>{addr}</p>
            </div>
          ))}
        </div>
      </Reveal>

      {/* ── Contact & Legal ── */}
      <section id="contact" style={{ backgroundColor: GREEN_DARK, color: '#fff', padding: '60px 40px' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '30px' }}>
          <div style={{ flex: '1', minWidth: '280px' }}>
            <h3 style={{ fontSize: '22px', marginBottom: '20px', color: GOLD }}>Contact Us</h3>
            <p><strong>Email:</strong> suadretailautomations@gmail.com</p>
            <p><strong>Call/WhatsApp:</strong> 9599419519, 8527250312, 7409102657</p>
            <p><strong>Address:</strong> Noida, Uttar Pradesh, India</p>
          </div>
          <div style={{ flex: '1', minWidth: '280px' }}>
            <h3 style={{ fontSize: '22px', marginBottom: '20px', color: GOLD }}>Policies</h3>
            <p><strong>Refund Policy:</strong> If a kiosk fails to dispense after a successful payment, refunds are automatically initiated within 5-7 business days to the original payment method.</p>
            <p><strong>Privacy Policy:</strong> We do not store any customer payment details. All payments are securely processed by Razorpay.</p>
          </div>
        </div>
        <div style={{
          borderTop: '1px solid rgba(255,255,255,0.15)', marginTop: '40px', paddingTop: '20px',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px',
        }}>
          <Link to="/?login=1" style={{
            color: GOLD, textDecoration: 'none', fontWeight: 700, fontSize: '14px',
            border: `1px solid ${GOLD}`, borderRadius: '8px', padding: '8px 20px',
          }}>
            🔐 Admin Login
          </Link>
          <p style={{ textAlign: 'center', fontSize: '14px', color: 'rgba(255,255,255,0.6)', margin: 0 }}>
            © 2026 Chill Ras. Made in India 🇮🇳. All rights reserved.
          </p>
        </div>
      </section>

    </div>
  );
}
